import { useState, useEffect } from 'react';
import { Modal, Box, Switch, RadioGroup, Radio, Button, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import useUpgrade from '../hooks/useUpgrade';
import { guardWrite } from '../utils/permissions';
import './rescrapingSettingsModal.css';   // reuse the website-links rescrape styling 1:1

const API = process.env.REACT_APP_API_BASE_URL;

// Purple MUI switch, matching the website-links rescrape settings.
const purpleSwitch = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
  '& .MuiSwitch-track': { backgroundColor: '#ccc' },
};

// Automatic product-catalog sync settings — the Products (Wix) analogue of the
// Knowledge Base → Website Links rescrape modal. Same styling/structure; just a
// simpler form (on/off + daily/monthly). Available on every plan EXCEPT free; the
// backend reports `plan_allowed`, and a free bot sees the upgrade screen instead.
export default function ProductSyncSettingsModal({
  open, onClose, botId, schedule, onSaved,
  endpoint = 'products/sync_schedule',                  // CMS reuses this modal with a different endpoint
  title, subtitle, upgradeTitle, upgradeDescription,    // optional copy overrides (default = products)
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { handleUpgradeClick } = useUpgrade();

  const planAllowed = schedule?.plan_allowed !== false;
  const [active, setActive] = useState(schedule?.active ?? true);
  const [interval, setIntervalVal] = useState(schedule?.interval || 'daily');
  const [saving, setSaving] = useState(false);

  // Re-seed from the latest fetched schedule each time the modal opens.
  useEffect(() => {
    if (open) {
      setActive(schedule?.active ?? true);
      setIntervalVal(schedule?.interval || 'daily');
    }
  }, [open, schedule]);

  if (!open) return null;

  const save = async () => {
    if (!guardWrite()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}${endpoint}?bot_id=${encodeURIComponent(botId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active, interval }),
      });
      if (!res.ok) throw new Error('save failed');
      const data = await res.json().catch(() => ({ active, interval, plan_allowed: true }));
      if (onSaved) onSaved(data);
      onClose();
    } catch (e) {
      console.error('Failed to save product sync schedule:', e);
      alert(t('psync_save_failed', 'Could not save. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const radioSx = {
    mr: -0.5,
    color: '#5616ea',
    '& .MuiSvgIcon-root': { transition: 'color 0.2s ease', color: '#5616ea' },
    '&.Mui-checked .MuiSvgIcon-root': { color: '#5616ea' },
    '&.Mui-disabled .MuiSvgIcon-root': { color: '#cfcfcf' },
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', outline: 'none' }}>
      <Box
        sx={{
          backgroundColor: 'white', borderRadius: '12px', padding: '20px', width: '100%',
          maxWidth: isMobile ? '80%' : '600px', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', outline: 'none', position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          aria-label="close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>

        {!planAllowed ? (
          <div className="rescraping-upgrade-modal-content">
            <div className="rescraping-upgrade-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="rescraping-upgrade-modal-title">{upgradeTitle || t('psync_upgrade_title', 'Keep your products up to date automatically')}</h2>
            <p className="rescraping-upgrade-modal-description">
              {upgradeDescription || t('psync_upgrade_description', 'Automatic product sync re-checks your store on a schedule so your assistant always has the latest catalog. Available on every paid plan.')}
            </p>
            <button className="rescraping-upgrade-button" onClick={() => handleUpgradeClick({ id: botId, wix_instance_id: true })}>
              {t('upgrade_now', 'Upgrade Now')}
            </button>
          </div>
        ) : (
          <>
            <h2 className="rs-title">{title || t('psync_title', 'Automatic product sync')}</h2>
            <p className="rs-subtitle" style={{ color: '#666', fontSize: '0.92rem', margin: '0 0 8px' }}>
              {subtitle || t('psync_subtitle', 'Keep your product catalog up to date automatically. We re-check your store on the schedule below.')}
            </p>

            {/* on/off */}
            <div className="rs-section rs-switch-row">
              <span className="rs-label-new-links">{t('psync_enable', 'Automatic sync')}</span>
              <div className="new-links-text-toggle">
                <Switch checked={active} onChange={(e) => setActive(e.target.checked)} sx={purpleSwitch} />
                <span className="rs-switch-text">{active ? t('on', 'On') : t('off', 'Off')}</span>
              </div>
            </div>

            {/* frequency */}
            <div className="rs-section">
              <span className="rs-label">{t('rescraping_frequency', 'Frequency')}</span>
              <RadioGroup
                row
                value={interval}
                onChange={(e) => setIntervalVal(e.target.value)}
                className="rs-radio-group"
                sx={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '0.5rem', opacity: active ? 1 : 0.5 }}
              >
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Radio value="daily" disabled={!active} sx={radioSx} />
                  <span
                    style={{ fontSize: '0.95rem', color: '#333', cursor: active ? 'pointer' : 'not-allowed' }}
                    onClick={() => active && setIntervalVal('daily')}
                  >
                    {t('daily', 'Daily')}
                  </span>
                </Box>
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Radio value="monthly" disabled={!active} sx={radioSx} />
                  <span
                    style={{ fontSize: '0.95rem', color: '#333', cursor: active ? 'pointer' : 'not-allowed' }}
                    onClick={() => active && setIntervalVal('monthly')}
                  >
                    {t('monthly', 'Monthly')}
                  </span>
                </Box>
              </RadioGroup>
            </div>

            {/* buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} marginTop={2}>
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{ color: '#5616ea', borderColor: '#5616ea', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } }}
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                variant="contained"
                sx={{ backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a14d6' } }}
              >
                {t('save', 'Save')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
